import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, ArrowLeft, ShieldCheck, Zap, ClipboardList } from 'lucide-react';
import productsData from '../data/products.json';
import { isVisibleProduct } from '../data/hiddenProducts';

const CATEGORY_DESCRIPTIONS: Record<string, string> = {
  all: "Explore our full range of premium commercial refrigeration solutions engineered for excellence and durability.",
  liebherr: "German-engineered refrigeration offering world-class efficiency, precision temperature control, and professional-grade build quality.",
  Arctique: "Premium Asia Pacific refrigeration solutions designed for maximum durability and performance in demanding commercial environments.",
  chest: "Spacious storage meets flexible display. Featuring smooth-sliding glass lids and superior insulation for retail efficiency.",
  hardtop: "Heavy-duty bulk storage solutions. Engineered with high-density insulation and robust hinges for maximum temperature stability.",
  upright: "Vertical efficiency with professional presence. Organized interiors and LED lighting provide maximum visibility and space optimization.",
  supermarket: "Asia Pacific retail solutions. High-impact open-front chillers and multi-door systems designed to enhance visibility and drive sales.",
  // Hidden for now along with the blast freezer products (see data/hiddenProducts.ts).
  // blast: "Fast-freezing technology for Asia Pacific logistics. Purpose-built for cold chain logistics and recharging eutectic plates for transport.",
  artisan: "Precision climate control for Asia Pacific bakeries. Stainless steel units that switch between chilling and freezing to preserve dough quality.",
  undercounter: "Compact under-counter refrigeration for tight kitchen spaces. Ideal for prep areas, bars, and small commercial setups with reliable performance and easy access.",
};

const MAIN_CATEGORIES = [
  { id: 'all', name: 'All Products' },
  { id: 'liebherr', name: 'Liebherr' },
  { id: 'arctique', name: 'Arctique' },
  { id: 'chest', name: 'Chest Type' },
  { id: 'hardtop', name: 'Hard Top' },
  { id: 'upright', name: 'Upright' },
  { id: 'supermarket', name: 'Supermarket Line' },
  // { id: 'blast', name: 'Blast Freezer' }, // hidden for now (see data/hiddenProducts.ts)
  { id: 'artisan', name: 'Artisan Bakery' },
  { id: 'undercounter', name: 'Under Counter Units' },
];

const SUPERMARKET_SUBCATEGORIES = [
  { id: 'supermarket-all', name: 'All Supermarket' },
  { id: 'supermarket-2door', name: '2 Doors' },
  { id: 'supermarket-3door', name: '3 Doors' },
  { id: 'supermarket-4door', name: '4 Doors' },
];

const ProductCard = ({ product, onSelect, index }: { product: any; onSelect: () => void; index: number }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: false, amount: 0.2 }}
    transition={{ duration: 0.4, delay: (index % 6) * 0.05 }}
    whileHover={{ y: -8 }}
    whileTap={{ scale: 0.98 }}
    onClick={onSelect}
    className="group flex flex-col h-full p-2.5 rounded-[32px] bg-[#f4f4f4] border border-white cursor-pointer shadow-[0_24px_48px_-16px_rgba(0,0,0,0.28)] hover:shadow-[0_36px_64px_-18px_rgba(0,0,0,0.35)] transition-shadow duration-500"
  >
    {/* Inset image panel */}
    <div className="relative aspect-[5/4] rounded-[24px] bg-white border border-black/[0.07] shadow-[inset_0_1px_2px_rgba(0,0,0,0.04)] p-8 flex items-center justify-center overflow-hidden">
      <img
        src={product.images[0]}
        alt={product.name}
        loading="lazy"
        decoding="async"
        className="max-h-full max-w-full object-contain transition-transform duration-700 group-hover:scale-105"
        onError={(e) => {
          e.currentTarget.src = `https://placehold.co/400x400/f8fafc/94a3b8?text=${encodeURIComponent(product.name)}`;
        }}
      />
    </div>

    {/* Name + status, action pill */}
    <div className="flex items-center justify-between gap-3 px-3.5 pt-4 pb-2.5 mt-auto">
      <div className="min-w-0">
        <h3 className="font-bold text-lg text-slate-900 leading-tight truncate" title={product.name}>
          {product.name}
        </h3>
        <p className="flex items-center gap-1.5 mt-0.5 text-sm text-slate-500 truncate capitalize">
          <span className="w-2 h-2 rounded-full bg-[#e1ae48] ring-2 ring-[#e1ae48]/25 flex-shrink-0" />
          {product.brand} · {product.group}
        </p>
      </div>
      <span className="flex-shrink-0 inline-flex items-center gap-1.5 h-10 px-4 rounded-full bg-[#1a1a1a] text-white text-sm font-medium group-hover:bg-[#172b81] transition-colors">
        <ClipboardList size={15} />
        View specs
      </span>
    </div>
  </motion.div>
);

const Products: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [activeCategory, setActiveCategory] = useState('all');
  const [supermarketSub, setSupermarketSub] = useState('supermarket-all');
  const [searchQuery, setSearchQuery] = useState('');
  const [itemsPerPage] = useState(12);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (location.state) {
      const state = location.state as { filterBrand?: string; filterCategory?: string };
      if (state.filterCategory && state.filterCategory !== 'all') {
        setActiveCategory(state.filterCategory);
      } else if (state.filterBrand) {
        setActiveCategory(state.filterBrand);
      }
      window.history.replaceState({}, document.title);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location.state]);

  const isSupermarketActive = activeCategory === 'supermarket';

  const filteredProducts = useMemo(() => {
    let filtered = productsData.filter(isVisibleProduct);

    if (activeCategory !== 'all') {
      filtered = filtered.filter((p: any) => {
        const brand = p.brand?.toLowerCase() || "";
        const group = p.group?.toLowerCase() || "";
        const name = p.name?.toLowerCase() || "";
        return (
          brand === activeCategory.toLowerCase() ||
          group === activeCategory.toLowerCase() ||  // exact match on group
          name.includes(activeCategory.toLowerCase())
        );
      });
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((p: any) =>
        p.name.toLowerCase().includes(q) || 
        p.group.toLowerCase().includes(q) || 
        p.brand.toLowerCase().includes(q)
      );
    }

    if (isSupermarketActive && supermarketSub !== 'supermarket-all') {
      const doors = supermarketSub.includes('2door') ? '2' : supermarketSub.includes('3door') ? '3' : '4';
      filtered = filtered.filter((p: any) => p.name.toLowerCase().includes(`${doors} door`));
    }

    return filtered;
  }, [activeCategory, supermarketSub, searchQuery, isSupermarketActive]);

  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const selectProduct = (name: string) => {
    navigate(`/products/details?name=${encodeURIComponent(name)}`);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory, supermarketSub, searchQuery]);

  return (
    <div className="bg-white min-h-screen w-full overflow-x-hidden font-inter selection:bg-black selection:text-white">
      
      {/* HERO SECTION */}
      <div className="relative h-[45vh] md:h-[55vh] overflow-hidden bg-slate-900 pt-24 md:pt-32">
        <img
          src="/images/Liebherr Glass.webp"
          alt="Cooling Solutions"
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover opacity-40 scale-110 object-[center_top]"
        />
        
        <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent" />
        
        <div className="relative z-10 flex flex-col items-center justify-center px-6 text-center h-full">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="text-white/70 text-[10px] font-black uppercase tracking-[0.5em] mb-4">Professional Catalog</p>
            <h1 className="text-5xl md:text-7xl font-black text-white tracking-tighter italic uppercase">
              {activeCategory === 'all' ? 'PREMIUM SOLUTIONS' : activeCategory.replace('-', ' ')}
            </h1>
          </motion.div>
        </div>
      </div>

      {/* FILTER BAR */}
      <div className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-screen-2xl mx-auto px-6 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
            <div className="flex flex-wrap items-center gap-2">
              {MAIN_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    setActiveCategory(cat.id);
                    if (cat.id !== 'supermarket') setSupermarketSub('supermarket-all');
                  }}
                  className={`px-6 py-2.5 rounded-full text-[11px] font-black uppercase tracking-widest transition-all border ${
                    activeCategory === cat.id
                      ? 'bg-black text-white border-black shadow-xl shadow-black/10'
                      : 'bg-white text-slate-400 border-slate-100 hover:border-slate-300'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="relative w-full lg:w-80">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <input
                type="text"
                placeholder="SEARCH MODELS..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-6 py-3 bg-slate-50 border-none rounded-full text-[11px] font-bold tracking-widest focus:ring-2 focus:ring-black transition-all"
              />
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-screen-2xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4 xl:col-span-3">
            <div className="sticky top-32 space-y-8">
              <motion.div 
                key={activeCategory}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-slate-900 rounded-[40px] p-10 text-white shadow-2xl relative overflow-hidden"
              >
                <div className="absolute -right-4 -top-4 w-32 h-32 bg-blue-600/20 blur-3xl rounded-full" />
                <span className="text-[10px] font-black uppercase tracking-[0.4em] text-blue-400 mb-6 block">Category Profile</span>
                <h2 className="text-4xl font-black tracking-tighter mb-6 capitalize italic">{activeCategory.replace('-', ' ')}</h2>
                <p className="text-slate-400 text-sm leading-relaxed mb-8 font-medium">
                  {CATEGORY_DESCRIPTIONS[activeCategory] || CATEGORY_DESCRIPTIONS.all}
                </p>
                <div className="space-y-4 border-t border-white/10 pt-8">
                  <div className="flex items-center gap-4 text-[10px] font-black tracking-widest text-slate-300">
                    <ShieldCheck size={18} className="text-blue-500" /> REGIONAL EXCELLENCE
                  </div>
                  <div className="flex items-center gap-4 text-[10px] font-black tracking-widest text-slate-300">
                    <Zap size={18} className="text-blue-500" /> ASIA PACIFIC COMPLIANT
                  </div>
                </div>
              </motion.div>

              {isSupermarketActive && (
                <div className="bg-white border border-slate-100 rounded-[32px] p-8 shadow-sm">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-6">Configuration</p>
                  <div className="space-y-2">
                    {SUPERMARKET_SUBCATEGORIES.map((sub) => (
                      <button
                        key={sub.id}
                        onClick={() => setSupermarketSub(sub.id)}
                        className={`w-full text-left px-5 py-4 rounded-2xl text-[11px] font-black uppercase tracking-widest transition-all flex items-center justify-between ${
                          supermarketSub === sub.id ? 'bg-slate-50 text-black' : 'text-slate-400 hover:bg-slate-50/50 hover:text-slate-600'
                        }`}
                      >
                        {sub.name}
                        {supermarketSub === sub.id && <div className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-slate-50 rounded-[32px] p-8 border border-slate-100 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Total Models</p>
                  <div className="text-4xl font-black text-black tracking-tighter">{filteredProducts.length}</div>
                </div>
                <div className="text-[10px] font-black text-blue-600 bg-blue-50 px-4 py-2 rounded-full">ACTIVE</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-8 xl:col-span-9">
            <AnimatePresence mode="wait">
              {paginatedProducts.length > 0 ? (
                <motion.div 
                  key={activeCategory + supermarketSub + searchQuery}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8"
                >
                  {paginatedProducts.map((product: any, i: number) => (
                    <ProductCard key={product.name} product={product} onSelect={() => selectProduct(product.name)} index={i} />
                  ))}
                </motion.div>
              ) : (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center py-40 text-center">
                  <Search className="text-slate-200 mb-6" size={64} />
                  <h3 className="text-2xl font-black text-black mb-2 uppercase tracking-tight">No results found</h3>
                  <p className="text-slate-400 text-sm mb-8">Try adjusting your filters or search term.</p>
                  <button onClick={() => {setActiveCategory('all'); setSearchQuery('');}} className="text-[11px] font-black uppercase tracking-[0.3em] text-blue-600 hover:text-blue-800">Clear all filters</button>
                </motion.div>
              )}
            </AnimatePresence>

            {totalPages > 1 && (
              <div className="mt-24 flex items-center justify-center gap-8">
                <button
                  onClick={() => {setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 400, behavior: 'smooth' });}}
                  disabled={currentPage === 1}
                  className="w-14 h-14 rounded-full border border-slate-200 flex items-center justify-center disabled:opacity-20 hover:bg-black hover:text-white transition-all"
                >
                  <ArrowLeft size={20} />
                </button>
                <span className="text-[11px] font-black tracking-[0.4em] text-black">PAGE {currentPage} / {totalPages}</span>
                <button
                  onClick={() => {setCurrentPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 400, behavior: 'smooth' });}}
                  disabled={currentPage === totalPages}
                  className="w-14 h-14 rounded-full border border-slate-200 flex items-center justify-center disabled:opacity-20 hover:bg-black hover:text-white transition-all rotate-180"
                >
                  <ArrowLeft size={20} />
                </button>
              </div>
            )}
          </div>
        </div>
      </main>

      <motion.footer
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: false }}
        transition={{ duration: 0.6 }}
        className="py-24 border-t border-slate-100 text-center bg-slate-50/50"
      >
        <p className="text-slate-300 text-[10px] font-black uppercase tracking-[0.6em]">Engineering the Cold Chain</p>
      </motion.footer>
    </div>
  );
};

export default Products;